import { useEffect, useMemo, useState } from 'react';
import { newId, koboToNaira, nairaToKobo } from '../lib/format';
import { compactNaira, computeNgxFees, type Side } from '../lib/ngx-fees';
import { BrokerApiError } from '../api/brokerClient';
import { evaluate as evalRisk, blocked as riskBlocked } from '../lib/pre-trade-risk';
import type { BrokerClient } from '../api/brokerClient';

interface Props {
  brokerId: string;
  accountId: string;
  brokerClient: BrokerClient;
  mfaSatisfied: boolean;
  symbol?: string;
  symbolLongName?: string;
  lastPrice?: number;
  pctChange?: number;
  buyingPowerKobo?: string;
  onPlaced?: (ack: { brokerOrderId: string }) => void;
  /** Pre-selects the side (e.g. when launched from a chart's Buy/Sell button). */
  defaultSide?: Side;
  /** Fires after the defaultSide has been applied, so the parent can clear it. */
  onSideChanged?: () => void;
  /** Extra live-quote + fundamental data shown in the stock-details strip. */
  bid?: number;
  ask?: number;
  bidSize?: number;
  askSize?: number;
  volume?: number;
  dayHigh?: number;
  dayLow?: number;
  weekHigh52?: number;
  weekLow52?: number;
  sector?: string;
  marketCap?: number;
  eps?: number;
  pe?: number;
}

type OrdType = 'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT' | 'MOC' | 'LOC' | 'TRAIL_STOP';
type Tif = 'DAY' | 'GTC' | 'IOC' | 'FOK';

const NEEDS_LIMIT: ReadonlySet<OrdType> = new Set(['LIMIT', 'STOP_LIMIT', 'LOC']);
const NEEDS_STOP: ReadonlySet<OrdType> = new Set(['STOP', 'STOP_LIMIT']);
const NEEDS_TRAIL: ReadonlySet<OrdType> = new Set(['TRAIL_STOP']);
const FORCE_DAY_TIF: ReadonlySet<OrdType> = new Set(['MOC', 'LOC']);

const ORDER_TYPE_LABEL: Record<OrdType, string> = {
  MARKET: 'MARKET',
  LIMIT: 'LIMIT',
  STOP: 'STOP',
  STOP_LIMIT: 'STOPLIMIT',
  MOC: 'MOC',
  LOC: 'LOC',
  TRAIL_STOP: 'TRAILSTOP',
};

export function OrderTicket({
  brokerId, accountId, brokerClient, mfaSatisfied,
  symbol: defaultSymbol = 'ZENITHBANK', symbolLongName = 'Zenith Bank',
  lastPrice, pctChange, buyingPowerKobo, onPlaced, defaultSide, onSideChanged,
  bid, ask, bidSize, askSize, volume,
  dayHigh, dayLow, weekHigh52, weekLow52,
  sector, marketCap, eps, pe,
}: Props) {
  const [side, setSide] = useState<Side>('BUY');
  // When a chart Buy/Sell button launches us with a side, adopt it once then
  // ping the parent so it can clear its prefill (letting the user freely flip
  // sides from this point onward).
  useEffect(() => {
    if (defaultSide && defaultSide !== side) { setSide(defaultSide); onSideChanged?.(); }
    else if (defaultSide) onSideChanged?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSide]);
  const [type, setType] = useState<OrdType>('LIMIT');
  const [tif, setTif] = useState<Tif>('DAY');
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [qty, setQty] = useState('100');
  const defaultPriceStr = lastPrice ? (lastPrice + 0.55).toFixed(2) : '111.47';
  const [price, setPrice] = useState(defaultPriceStr);
  const [stop, setStop] = useState(lastPrice ? (lastPrice - 0.50).toFixed(2) : '110.50');
  const [trail, setTrail] = useState('1.00');
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const [last, setLast] = useState<{ ok: true; brokerOrderId: string } | undefined>();

  if (FORCE_DAY_TIF.has(type) && tif !== 'DAY') setTif('DAY');

  const limitKobo = NEEDS_LIMIT.has(type) ? nairaToKobo(price)
    : (type === 'MARKET' && lastPrice ? nairaToKobo(lastPrice.toFixed(2)) : undefined);
  const stopKobo = NEEDS_STOP.has(type) ? nairaToKobo(stop) : undefined;
  const trailKobo = NEEDS_TRAIL.has(type) ? nairaToKobo(trail) : undefined;

  const refPriceKobo = limitKobo
    ?? stopKobo
    ?? (lastPrice ? nairaToKobo(lastPrice.toFixed(2)) : undefined);
  const qtyNum = Number(qty);

  const valid = useMemo(() => {
    if (!/^[A-Z][A-Z0-9]{1,9}$/.test(symbol)) return 'Invalid NGX symbol';
    if (!Number.isInteger(qtyNum) || qtyNum <= 0) return 'Quantity must be a positive integer';
    if (NEEDS_LIMIT.has(type) && !limitKobo) return 'Invalid limit price';
    if (NEEDS_STOP.has(type) && !stopKobo) return 'Invalid stop price';
    if (NEEDS_TRAIL.has(type) && !trailKobo) return 'Invalid trailing offset';
    if (!mfaSatisfied) return 'MFA required to place orders';
    return undefined;
  }, [symbol, qtyNum, type, limitKobo, stopKobo, trailKobo, mfaSatisfied]);

  const fees = useMemo(() => {
    if (!refPriceKobo || qtyNum <= 0 || !Number.isInteger(qtyNum)) return undefined;
    try { return computeNgxFees(side, qtyNum, refPriceKobo); } catch { return undefined; }
  }, [side, qtyNum, refPriceKobo]);

  const risk = (() => {
    try {
      if (!fees) return [] as ReturnType<typeof evalRisk>;
      return evalRisk({
        side, symbol,
        considerationKobo: BigInt(fees.totalCost),
        buyingPowerKobo: buyingPowerKobo ? BigInt(buyingPowerKobo) : undefined,
      });
    } catch { return []; }
  })();
  const hardBlocked = riskBlocked(risk);

    async function place() {
    if (valid) return;
    setSubmitting(true); setErr(undefined);
    try {
      const clOrdId = newId('clord');
      const idempotencyKey = newId('idem');
      const payload: Record<string, unknown> = {
        clOrdId, idempotencyKey, accountId, symbol,
        side, type, quantity: qtyNum, timeInForce: tif,
      };
      if (NEEDS_LIMIT.has(type) && limitKobo) payload.limitPrice = limitKobo;
      if (NEEDS_STOP.has(type) && stopKobo) payload.stopPrice = stopKobo;
      if (NEEDS_TRAIL.has(type) && trailKobo) payload.trailingOffset = trailKobo;

      // Pre-trade confirmation
      const confirmed = window.confirm(
        `Confirm ${side} order:\n${qtyNum} shares of ${symbol}\nOrder Type: ${type}\n${
          limitKobo ? `Limit Price: ₦${(limitKobo / 100).toFixed(2)}\n` : ''
        }${stopKobo ? `Stop Price: ₦${(stopKobo / 100).toFixed(2)}\n` : ''}Continue?`
      );
      if (!confirmed) {
        setSubmitting(false);
        return;
      }

      const ack = await brokerClient.placeOrder(brokerId, payload);
      const ackId = String(ack.brokerOrderId);
      setLast({ ok: true, brokerOrderId: ackId });
      onPlaced?.({ brokerOrderId: ackId });
      setReviewing(false);
    } catch (e) {
      if (e instanceof BrokerApiError) {
        if (e.code === 'in_progress') setErr('A duplicate order is still being processed; try again in a moment.');
        else if (e.code === 'mfa_required') setErr('MFA is required for trading. Please re-authenticate.');
        else if (e.code === 'insufficient_funds') setErr('Insufficient buying power.');
        else if (e.code === 'symbol_suspended') setErr('Trading in this symbol is suspended.');
        else setErr('Order rejected: ' + e.code);
      } else {
        setErr('Network error placing order.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const submitButtonLabel = submitting
    ? 'Placing…'
    : side + ' ' + (qtyNum || 0) + ' ' + symbol + ' (Skip)';

  return (
    <div className="ticket-v2" role="region" aria-label="Order Entry">
      <h3 className="te-title">ORDER ENTRY</h3>

      <div className="te-symbol-card">
        <div className="te-symbol-row">
          <input
            aria-label="Symbol"
            className="te-symbol"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            maxLength={10}
          />
          <span className="te-longname">{symbolLongName}</span>
        </div>
        <div className="te-quote">
          <span>Last: <strong>{lastPrice ? lastPrice.toFixed(2) : '\u2014'}</strong></span>
          <span>Chg: <strong className={(pctChange ?? 0) >= 0 ? 'up' : 'down'}>
            {pctChange != null ? (pctChange >= 0 ? '+' : '') + pctChange.toFixed(2) + '%' : '\u2014'}
          </strong></span>
        </div>
        <div className="te-details">
          <div className="te-detail"><span className="k">Bid</span><span className="v up">{bid != null ? bid.toFixed(2) : '\u2014'}{bidSize ? <small> x{bidSize}</small> : null}</span></div>
          <div className="te-detail"><span className="k">Ask</span><span className="v down">{ask != null ? ask.toFixed(2) : '\u2014'}{askSize ? <small> x{askSize}</small> : null}</span></div>
          <div className="te-detail"><span className="k">Vol</span><span className="v">{volume != null ? (volume >= 1_000_000 ? (volume / 1_000_000).toFixed(1) + 'M' : volume >= 1_000 ? (volume / 1_000).toFixed(1) + 'K' : String(volume)) : '\u2014'}</span></div>
          <div className="te-detail"><span className="k">Day H/L</span><span className="v mono">{dayHigh != null && dayLow != null ? dayHigh.toFixed(2) + ' / ' + dayLow.toFixed(2) : '\u2014'}</span></div>
          <div className="te-detail"><span className="k">52W H/L</span><span className="v mono">{weekHigh52 != null && weekLow52 != null ? weekHigh52.toFixed(2) + ' / ' + weekLow52.toFixed(2) : '\u2014'}</span></div>
          <div className="te-detail"><span className="k">P/E</span><span className="v">{pe != null ? pe.toFixed(2) : '\u2014'}</span></div>
          <div className="te-detail"><span className="k">EPS</span><span className="v">{eps != null ? eps.toFixed(2) : '\u2014'}</span></div>
          <div className="te-detail"><span className="k">Mkt Cap</span><span className="v">{marketCap != null ? '\u20A6' + marketCap.toFixed(1) + 'B' : '\u2014'}</span></div>
          <div className="te-detail"><span className="k">Sector</span><span className="v text">{sector ?? '\u2014'}</span></div>
        </div>
      </div>

      <div className="te-side" role="radiogroup" aria-label="Side">
        <button
          type="button" role="radio" aria-checked={side === 'BUY'}
          className={'te-side-btn buy ' + (side === 'BUY' ? 'active' : '')}
          onClick={() => setSide('BUY')}
        >BUY</button>
        <button
          type="button" role="radio" aria-checked={side === 'SELL'}
          className={'te-side-btn sell ' + (side === 'SELL' ? 'active' : '')}
          onClick={() => setSide('SELL')}
        >SELL</button>
      </div>

      <label className="te-label" htmlFor="te-type">ORDER TYPE</label>
      <select id="te-type" className="te-input" value={type} onChange={(e) => setType(e.target.value as OrdType)}>
        {(['MARKET', 'LIMIT', 'MOC', 'LOC', 'STOP', 'STOP_LIMIT', 'TRAIL_STOP'] as const).map((t) => (
          <option key={t} value={t}>{ORDER_TYPE_LABEL[t]}</option>
        ))}
      </select>

      <label className="te-label" htmlFor="te-qty">QUANTITY</label>
      <input
        id="te-qty" className="te-input" inputMode="numeric"
        value={qty} onChange={(e) => setQty(e.target.value.replace(/[^\d]/g, ''))}
      />

      <label className="te-label" htmlFor="te-tif">TIME IN FORCE</label>
      <select
        id="te-tif" className="te-input" value={tif}
        onChange={(e) => setTif(e.target.value as Tif)}
        disabled={FORCE_DAY_TIF.has(type)}
        title={FORCE_DAY_TIF.has(type) ? 'Auction-bound order types execute on the closing print only' : undefined}
      >
        <option value="DAY">DAY</option>
        <option value="GTC">GTC</option>
        <option value="IOC">IOC</option>
        <option value="FOK">FOK</option>
      </select>

      {NEEDS_LIMIT.has(type) && (
        <>
          <label className="te-label" htmlFor="te-price">LIMIT PRICE</label>
          <input
            id="te-price" className="te-input" inputMode="decimal"
            value={price} onChange={(e) => setPrice(e.target.value)}
          />
        </>
      )}
      {NEEDS_STOP.has(type) && (
        <>
          <label className="te-label" htmlFor="te-stop">STOP PRICE</label>
          <input
            id="te-stop" className="te-input" inputMode="decimal"
            value={stop} onChange={(e) => setStop(e.target.value)}
          />
        </>
      )}
      {NEEDS_TRAIL.has(type) && (
        <>
          <label className="te-label" htmlFor="te-trail">TRAILING OFFSET (₦)</label>
          <input
            id="te-trail" className="te-input" inputMode="decimal"
            value={trail} onChange={(e) => setTrail(e.target.value)}
          />
        </>
      )}

      {fees ? (
        <div className="te-fees" role="region" aria-label={'NGX fees on ' + side}>
          <div className="te-fees-h">NGX FEES ({side})</div>
          <FeeRow k="Consideration" v={compactNaira(fees.consideration)} />
          <FeeRow k={'Brokerage (' + fees.rates.brokeragePct + '%)'} v={koboToNaira(fees.brokerage)} />
          <FeeRow k={'VAT on Brokerage (' + fees.rates.vatBrokeragePct + '%)'} v={koboToNaira(fees.vatOnBrokerage)} />
          <FeeRow k={'SEC Fee (' + fees.rates.secPct + '%)'} v={koboToNaira(fees.secFee)} />
          <FeeRow k={'VAT on SEC (' + fees.rates.vatSecPct + '%)'} v={koboToNaira(fees.vatOnSec)} />
          <FeeRow k={'CSCS Fee (' + fees.rates.cscsPct + '%)'} v={koboToNaira(fees.cscsFee)} />
          <FeeRow k={'VAT on CSCS (' + fees.rates.vatCscsPct + '%)'} v={koboToNaira(fees.vatOnCscs)} />
          {side === 'BUY' && <FeeRow k={'Stamp Duty (' + fees.rates.stampDutyPct + '%)'} v={koboToNaira(fees.stampDuty)} />}
          <FeeRow k={'Terminal Fee (' + fees.rates.terminalPct + '%)'} v={koboToNaira(fees.terminalFee)} />
          <div className="te-fees-divider" />
          <FeeRow k="Total Fees & Charges" v={koboToNaira(fees.totalFees)} bold className="down" />
          <FeeRow k="Total Cost" v={compactNaira(fees.totalCost)} bold />
          {buyingPowerKobo && (
            <FeeRow
              k="Available Buying Power"
              v={compactNaira(buyingPowerKobo)}
              className={
                side === 'BUY' && BigInt(fees.totalCost) > BigInt(buyingPowerKobo) ? 'down' : 'up'
              }
            />
          )}
        </div>
      ) : (
        <div className="te-hint">Enter a valid quantity and price to see fees.</div>
      )}

      {valid && <div className="te-hint">{valid}</div>}
      {err && <div className="te-err">{err}</div>}
      {last && <div className="te-hint">Acked. broker_order_id={last.brokerOrderId}</div>}

      {risk.length > 0 && (
        <div className="te-risk" role="region" aria-label="Pre-trade risk">
          {risk.map((f) => (
            <div key={f.code} className={'te-risk-row sev-' + f.severity}>
              <span className="te-risk-code">{f.code}</span> {f.message}
            </div>
          ))}
        </div>
      )}

      <div className="te-actions">
        <button
          type="button"
          className={'te-review ' + (side === 'SELL' ? 'sell' : 'buy')}
          disabled={submitting || !!valid}
          onClick={() => setReviewing(true)}
        >REVIEW ORDER</button>
        <button
          type="button"
          className={'te-skip ' + (side === 'SELL' ? 'sell' : 'buy')}
          disabled={submitting || !!valid}
          onClick={() => void place()}
          title="Submit immediately, skipping the confirmation modal"
        >{submitButtonLabel}</button>
      </div>

      {reviewing && fees && (
        <div className="te-modal" role="dialog" aria-modal="true" aria-label="Review order">
          <div className="te-modal-card">
            <h4>Confirm {side}</h4>
            <p className="te-modal-line">
              <span style={{ color: side === 'BUY' ? 'var(--green)' : 'var(--red)' }}>{side}</span>
              {' '}<strong>{qtyNum.toLocaleString('en-NG')}</strong> {symbol} {NEEDS_LIMIT.has(type) ? '@ ' + price : '(' + ORDER_TYPE_LABEL[type] + ')'}
            </p>
            <div className="te-modal-line"><span>Total {side === 'BUY' ? 'Cost' : 'Proceeds'}:</span> <strong>{compactNaira(fees.totalCost)}</strong></div>
            <div className="te-modal-line"><span>Fees:</span> <strong className="down">{koboToNaira(fees.totalFees)}</strong></div>
            <div className="te-modal-line"><span>TIF:</span> <strong>{tif}</strong></div>
            <div className="te-modal-buttons">
              <button onClick={() => setReviewing(false)} disabled={submitting}>Cancel</button>
              <button className={'te-confirm ' + (side === 'SELL' ? 'sell' : 'buy')} onClick={() => void place()} disabled={submitting}>
                {submitting ? 'Placing…' : 'Confirm ' + side}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeeRow({ k, v, bold, className }: { k: string; v: string; bold?: boolean; className?: string }) {
  return (
    <div className="te-fee-row">
      <span className={'k ' + (bold ? 'bold' : '')}>{k}</span>
      <span className={'v mono ' + (bold ? 'bold ' : '') + (className ?? '')}>{v}</span>
    </div>
  );
}

